import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowRight, MessageCircle } from "lucide-react";
import { getWishlistProducts, waLink, fmtPrice, skuFromHandle, productUrl } from "../lib/store";
import { trackContactClick } from "../lib/analytics";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";
import { CreditNotice } from "../components/CreditNotice";
import { Button } from "../components/ui/button";

function wishlistQuoteText(items) {
  const shown = items.slice(0, 20);
  const lines = shown.map(
    (p, i) => `${i + 1}. ${p.title} — ${fmtPrice(p.price)} (SKU: ${skuFromHandle(p.handle)})\n${productUrl(p.handle)}`
  );
  const more = items.length > 20 ? `\n…and ${items.length - 20} more pieces` : "";
  return `Hello Regal Jewels! I'd like a quote for my wishlist (${items.length} ${items.length === 1 ? "piece" : "pieces"}):\n\n${lines.join("\n\n")}${more}`;
}

export default function Wishlist() {
  const [items, setItems] = useState(getWishlistProducts);

  useEffect(() => {
    window.scrollTo(0, 0);
    const sync = () => setItems(getWishlistProducts());
    window.addEventListener("rj-wishlist", sync);
    return () => window.removeEventListener("rj-wishlist", sync);
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14" data-testid="wishlist-page">
      <Seo title="My Wishlist" description="Your saved pieces at Regal Jewels, Chicago." path="/wishlist" noindex />
      <div className="text-center mb-12">
        <p className="section-eyebrow">Saved For You</p>
        <h1 className="section-title">My Wishlist</h1>
        {items.length > 0 && (
          <>
            <p className="text-sm text-[#8f8571] mt-4">{items.length} saved {items.length === 1 ? "piece" : "pieces"} — tap the heart on any piece to remove it</p>
            <div className="flex justify-center mt-3"><CreditNotice placement="catalog" /></div>
            <a
              href={waLink(wishlistQuoteText(items))}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackContactClick("whatsapp_wishlist_quote")}
              className="inline-block mt-7"
            >
              <Button className="rounded-none bg-[#25D366] hover:bg-[#1fb457] text-white tracking-[0.15em] uppercase text-xs px-8 py-4 h-auto transition-colors duration-300" data-testid="wishlist-whatsapp-quote-btn">
                <MessageCircle size={15} className="mr-2" /> Get a Quote for My Wishlist on WhatsApp
              </Button>
            </a>
          </>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20" data-testid="wishlist-empty-state">
          <Heart size={36} strokeWidth={1.2} className="mx-auto text-[#cdb87a]" />
          <p className="font-heading text-2xl text-[#3a352c] mt-6">Your wishlist is empty</p>
          <p className="text-[#8f8571] mt-2 max-w-md mx-auto">Tap the heart on any piece you love and it will be saved here for your next visit.</p>
          <Link to="/shop">
            <Button className="mt-8 rounded-none bg-[#1a1713] hover:bg-[#3a2f1d] text-[#f3ead2] tracking-[0.2em] uppercase text-xs px-9 py-4 h-auto transition-colors duration-300">
              Browse the Collection <ArrowRight size={13} className="ml-2" />
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8">
          {items.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}
